import * as React from 'react'
import { useEscapeListener } from '@ds/react-utils'

import {
  useAriaExpanded,
  useAriaControls,
  useOutsideFocusListener,
  useOutsideClickListener,
} from './internal/utils'

export interface UseDisclosureArgs {
  /** The element containing the content the trigger element will reveal/hide */
  disclosureElement?: HTMLElement | null
  /** A unique ID to be applied to the disclosureElement.  If one is not provided a unique ID will be generated and applied */
  id?: string
  /**
   * closeOnEscape closes the disclosure when Escape is pressed.
   * closeOnFocusLoss closes the disclosure when focus leaves the trigger/disclosure.
   * closeOnOutsideClick closes the disclosure when the clicks outside the trigger/disclosure.
   */
  options?: {
    closeOnEscape?: boolean
    closeOnFocusLoss?: boolean
    closeOnOutsideClick?: boolean
  }
  /** The element that when activated toggles the disclosure content visibility. */
  triggerElement?: HTMLElement | null
}

/**
 * useDisclosure is a utility to create an accessible disclosure
 * when given a trigger button a container of disclosure content.
 *
 * This hook was designed with navigation (popover) disclosures
 * in mind and may not be appropriate for inline disclosures e.g. Accordion
 *
 * Specifically it does the following:
 *
 * - provides an onClick that should be attached to the trigger button
 *   that will toggle the visible state of the disclosure
 *
 * - manages aria-expanded on the trigger
 *
 * - manages aria-controls between trigger & disclosure the hook
 *   will attempt to use the id on the disclosure container if present
 *   otherwise you can specify one or one will be generated
 */
export const useDisclosure = ({
  disclosureElement,
  id,
  options,
  triggerElement,
}: UseDisclosureArgs) => {
  const closeOnEscape = options?.closeOnEscape ?? false
  const closeOnFocusLoss = options?.closeOnFocusLoss ?? false
  const closeOnOutsideClick = options?.closeOnOutsideClick ?? false

  const [visible, setVisible] = React.useState(false)
  const closeDisclosure = React.useCallback(() => {
    setVisible(false)
  }, [])
  const toggleDisclosure = React.useCallback(() => {
    setVisible((curr) => !curr)
  }, [])

  const closeAndFocusTrigger = React.useCallback(() => {
    triggerElement?.focus({ preventScroll: true })
    closeDisclosure()
  }, [closeDisclosure, triggerElement])
  useEscapeListener(closeAndFocusTrigger, closeOnEscape && visible)

  useOutsideClickListener({
    elements: [disclosureElement, triggerElement],
    enabled: closeOnOutsideClick && visible,
    handler: closeDisclosure,
  })

  useOutsideFocusListener({
    elements: [disclosureElement, triggerElement],
    enabled: closeOnFocusLoss && visible,
    handler: closeDisclosure,
  })

  useAriaExpanded({
    expanded: visible,
    triggerElement,
  })

  useAriaControls({
    controlledElement: disclosureElement,
    controllingElement: triggerElement,
    id,
  })

  return {
    triggerProps: {
      onClick: toggleDisclosure,
    },
    disclosureProps: {
      visible,
    },
    closeDisclosure,
  }
}
